export type FileReference = { __bilaflowFile: 'r2-v1'; key: string; type: string; sha256?: string; size?: number };
export const isFileReference = (value: unknown): value is FileReference => Boolean(value && typeof value === 'object'
  && (value as FileReference).__bilaflowFile === 'r2-v1' && typeof (value as FileReference).key === 'string');

// A local-only string preserves the attachment's existing string-valued field
// and its exact cloud reference without pretending to contain original bytes.
// It is never used as an image/download URL or sent to the cloud as a string.
const prefix = 'bilaflow-missing-r2-v1:';
export const missingCloudFile = (reference: FileReference) => `${prefix}${encodeURIComponent(JSON.stringify(reference))}`;
export const missingCloudFileReference = (value: unknown): FileReference | null => {
  if (typeof value !== 'string' || !value.startsWith(prefix)) return null;
  try {
    const decoded: unknown = JSON.parse(decodeURIComponent(value.slice(prefix.length)));
    return isFileReference(decoded) ? decoded : null;
  } catch { return null; }
};
export const isMissingCloudFile = (value: unknown) => missingCloudFileReference(value) !== null;
const remotePrefix = 'bilaflow-remote-r2-v1:';
export const remoteCloudFile = (reference: FileReference) => `${remotePrefix}${encodeURIComponent(JSON.stringify(reference))}`;
export const remoteCloudFileReference = (value: unknown): FileReference | null => {
  if (typeof value !== 'string' || !value.startsWith(remotePrefix)) return null;
  try {
    const decoded: unknown = JSON.parse(decodeURIComponent(value.slice(remotePrefix.length)));
    return isFileReference(decoded) ? decoded : null;
  } catch { return null; }
};
export const cloudReferenceKeys = (value: unknown, keys = new Set<string>()): Set<string> => {
  if (isFileReference(value)) keys.add(value.key);
  else if (value && typeof value === 'object') Object.values(value).forEach((item) => cloudReferenceKeys(item, keys));
  return keys;
};
export const missingCloudFileKeys = (value: unknown, keys = new Set<string>()): Set<string> => {
  const reference = missingCloudFileReference(value);
  if (reference) keys.add(reference.key);
  else if (value && typeof value === 'object') Object.values(value).forEach((item) => missingCloudFileKeys(item, keys));
  return keys;
};
export const resolveMissingCloudFiles = <T>(value: T, available: Map<string, string>): T => {
  const reference = missingCloudFileReference(value);
  if (reference) return (available.get(reference.key) ?? value) as T;
  if (Array.isArray(value)) return value.map((item) => resolveMissingCloudFiles(item, available)) as T;
  if (value && typeof value === 'object') return Object.fromEntries(Object.entries(value).map(([key, item]) => [key, resolveMissingCloudFiles(item, available)])) as T;
  return value;
};
